import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  formatVsBenchmark,
  relativeStrengthScore,
  RS_VOLUME_METER_SCALE,
  strengthMeterValue,
} from '../../utils/relativeStrength';

function withAlpha(hex, alpha) {
  const raw = String(hex || '').replace('#', '');
  if (raw.length !== 6) return hex;
  const value = Math.round(Math.max(0, Math.min(1, alpha)) * 255).toString(16).padStart(2, '0');
  return `#${raw}${value}`;
}

const PIN_AMBER = '#E0B84D';

function pinColorForScore(score, colors) {
  if (!Number.isFinite(score)) return colors.textMuted;
  if (score >= 51) return colors.success;
  if (score <= 45) return colors.danger;
  return PIN_AMBER;
}

function pinGlow(color, ring) {
  return `0 0 0 2px ${ring}, 0 0 8px ${withAlpha(color, 0.65)}, 0 0 14px ${withAlpha(color, 0.28)}`;
}

function polar(cx, cy, radius, angle) {
  return {
    x: cx + radius * Math.cos(angle),
    y: cy - radius * Math.sin(angle),
  };
}

function upperArc(cx, cy, radius, startAngle, endAngle) {
  const start = polar(cx, cy, radius, startAngle);
  const end = polar(cx, cy, radius, endAngle);
  // sweep-flag 1: left end → right end the clockwise way, i.e. over the top. With 0 the
  // band bows below the hub and everything but the two end caps falls outside the viewBox.
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${radius} ${radius} 0 0 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}

function scoreToAngle(score) {
  const clamped = Math.max(0, Math.min(100, Number(score) || 0));
  return Math.PI * (1 - (clamped / 100));
}

function CircularStrengthGauge({
  needles,
  colors,
  loading,
  width,
}) {
  const gradientId = useId().replace(/:/g, '');
  const glowId = `${gradientId}-glow`;
  const size = Math.max(148, width);
  const cx = size / 2;
  const cy = size * 0.58;
  const radius = size * 0.38;
  const stroke = Math.max(12, size * 0.07);
  const trackStroke = stroke + 6;
  const pinRadius = Math.max(6, stroke * 0.42);
  // A full line below the arc's round end caps. Sitting level with them, the words
  // washed out any needle dot parked at an extreme.
  const scaleLabelY = cy + (trackStroke / 2) + pinRadius + 14;
  const svgHeight = scaleLabelY + 5;
  // 50 is the top of a half-circle, not the bottom middle — tick and label belong up there.
  const midTickOuter = cy - radius - (trackStroke / 2) - 4;
  const midTickInner = cy - radius + (stroke / 2) + 2;
  const primary = needles[0];

  return (
    <svg
      data-testid="strength-odometer-gauge"
      width="100%"
      height={svgHeight}
      viewBox={`0 0 ${size} ${svgHeight}`}
      role="img"
      aria-label={
        loading
          ? 'Market strength loading'
          : `${primary?.label || 'Market strength'} ${Math.round(primary?.actualValue ?? primary?.value ?? 50)}${primary?.strengthLabel ? `, ${primary.strengthLabel}` : ''}`
      }
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={colors.danger} />
          <stop offset="48%" stopColor={PIN_AMBER} />
          <stop offset="100%" stopColor={colors.success} />
        </linearGradient>
        <filter id={glowId} x="-25%" y="-25%" width="150%" height="150%">
          <feGaussianBlur stdDeviation="1.4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <path
        data-testid="strength-odometer-track"
        d={upperArc(cx, cy, radius, Math.PI, 0)}
        fill="none"
        stroke={withAlpha(colors.textMuted || '#7B8A9E', 0.22)}
        strokeWidth={trackStroke}
        strokeLinecap="round"
      />
      <path
        d={upperArc(cx, cy, radius, Math.PI, 0)}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth={stroke}
        strokeLinecap="round"
        opacity={loading ? 0.35 : 0.92}
      />
      <line
        x1={cx}
        y1={midTickOuter}
        x2={cx}
        y2={midTickInner}
        stroke={colors.textMuted}
        strokeWidth="1.5"
        opacity="0.7"
      />
      <text
        x={cx}
        y={midTickOuter - 5}
        fill={colors.textMuted}
        fontSize="11"
        fontWeight="600"
        textAnchor="middle"
      >
        50
      </text>

      {needles.map((needle) => {
        const angle = scoreToAngle(needle.value);
        const pin = polar(cx, cy, radius, angle);
        const tip = polar(cx, cy, radius - stroke * 0.15, angle);
        return (
          <g key={needle.key} opacity={loading ? 0.35 : 1}>
            <line
              x1={cx}
              y1={cy}
              x2={tip.x}
              y2={tip.y}
              stroke={needle.color}
              strokeWidth={needle.dashed ? 2 : 2.5}
              strokeLinecap="round"
              strokeDasharray={needle.dashed ? '5 4' : undefined}
              filter={loading ? undefined : `url(#${glowId})`}
            />
            <circle
              data-testid={needle.testId}
              data-series-key={needle.key}
              cx={pin.x}
              cy={pin.y}
              r={pinRadius}
              // Hollow dot for the dashed reference series so the two never read as one.
              fill={needle.dashed ? colors.bgCard : needle.color}
              stroke={needle.dashed ? needle.color : colors.bgCard}
              strokeWidth={needle.dashed ? 2.5 : 2}
              filter={loading ? undefined : `url(#${glowId})`}
            />
          </g>
        );
      })}

      <circle
        cx={cx}
        cy={cy}
        r="5"
        fill={colors.bgCard}
        stroke={primary?.color || colors.textMuted}
        strokeWidth="2"
      />
      <text
        x={cx - radius}
        y={scaleLabelY}
        fill={colors.danger}
        fontSize="11"
        fontWeight="700"
        textAnchor="middle"
      >
        Weak
      </text>
      <text
        x={cx + radius}
        y={scaleLabelY}
        fill={colors.success}
        fontSize="11"
        fontWeight="700"
        textAnchor="middle"
      >
        Strong
      </text>
    </svg>
  );
}

function StrengthTrack({ colors, height = 4, children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        position: 'relative',
        height,
        borderRadius: 999,
        backgroundColor: withAlpha(colors.textMuted || '#7B8A9E', 0.28),
      }}
    >
      <div
        data-testid="strength-odometer-midpoint"
        aria-hidden
        style={{
          position: 'absolute',
          left: '50%',
          top: -4,
          width: 1,
          height: height + 8,
          backgroundColor: colors.textMuted,
          opacity: 0.8,
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
        }}
      />
      {children}
    </div>
  );
}

/**
 * Relative strength meter.
 * Prefer score100 (0–100) for needle position when provided.
 * scorePct is the volume-weighted excess % used when score100 is omitted.
 * Use size="mini" for compact row scanners (bar + needle only).
 *
 * `series` turns the circular variant into an interactive multi-reading gauge. The legend
 * keeps every reading visible while hover, focus, or tap chooses the one needle on the arc.
 * Each entry is
 * `{ key, label, value, gaugeScore, strengthLabel, dashed }` — `value` is what the legend
 * prints, `gaugeScore` is where the needle sits on the 0–100 arc (they differ when an
 * indicator's practical range is not 0–100).
 */
export function StrengthOdometer({
  scorePct = null,
  score100 = null,
  benchmark = null,
  loading = false,
  label = 'Relative strength',
  strengthLabel = null,
  headerControl = null,
  title: titleProp,
  height = 88,
  size = 'default',
  variant = 'bar',
  series = null,
}) {
  const { colors } = useTheme();
  const resolvedScore100 = Number.isFinite(score100)
    ? score100
    : relativeStrengthScore(scorePct);
  const target = Number.isFinite(resolvedScore100) ? resolvedScore100 : 50;
  const meter = Number.isFinite(resolvedScore100)
    ? (resolvedScore100 / 50) - 1 // map 0..100 → -1..1
    : strengthMeterValue(scorePct, RS_VOLUME_METER_SCALE);
  const isMini = size === 'mini';
  const isCircular = variant === 'circular' && !isMini;
  const hasSeries = isCircular && Array.isArray(series) && series.length > 0;
  const formatted = formatVsBenchmark(scorePct, benchmark || 'SPY', { compact: !benchmark });

  const gaugeTargets = useMemo(() => (
    hasSeries
      ? series.map((entry) => (Number.isFinite(entry.gaugeScore) ? entry.gaugeScore : 50))
      : [target]
  ), [hasSeries, series, target]);
  const targetsKey = gaugeTargets.join('|');
  const hasReading = hasSeries
    ? series.some((entry) => Number.isFinite(entry.gaugeScore))
    : Number.isFinite(resolvedScore100);

  const [displays, setDisplays] = useState(() => gaugeTargets.map(() => 50));
  const [selectedSeriesKey, setSelectedSeriesKey] = useState(null);
  const [previewSeriesKey, setPreviewSeriesKey] = useState(null);
  const displaysRef = useRef(displays);
  displaysRef.current = displays;

  useEffect(() => {
    if (isMini) return undefined;
    const targets = targetsKey.split('|').map(Number);
    if (loading || !hasReading) {
      setDisplays(targets.map(() => 50));
      return undefined;
    }
    let raf = 0;
    const start = performance.now();
    const from = displaysRef.current;
    const duration = 550;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      setDisplays(targets.map((to, index) => {
        const fromValue = Number.isFinite(from[index]) ? from[index] : 50;
        return fromValue + (to - fromValue) * eased;
      }));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // Only re-animate when a target score changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetsKey, loading, isMini, hasReading]);

  const display = displays[0] ?? 50;
  const needlePct = ((meter + 1) / 2) * 100;
  const defaultSeriesKey = hasSeries ? series[0]?.key : null;
  const persistedSeriesKey = hasSeries && series.some((entry) => entry.key === selectedSeriesKey)
    ? selectedSeriesKey
    : defaultSeriesKey;
  const activeSeriesKey = previewSeriesKey || persistedSeriesKey;
  const activeSeriesIndex = hasSeries
    ? Math.max(0, series.findIndex((entry) => entry.key === activeSeriesKey))
    : 0;
  const activeSeries = hasSeries ? series[activeSeriesIndex] : null;
  const valueColor = pinColorForScore(
    hasSeries ? activeSeries?.gaugeScore : resolvedScore100,
    colors,
  );

  const needles = hasSeries
    ? [{
        key: activeSeries.key,
        label: activeSeries.label,
        actualValue: activeSeries.value,
        strengthLabel: activeSeries.strengthLabel,
        value: displays[activeSeriesIndex] ?? 50,
        color: pinColorForScore(activeSeries.gaugeScore, colors),
        dashed: !!activeSeries.dashed,
        testId: 'strength-odometer-needle',
      }]
    : [{
        key: 'primary',
        value: display,
        color: valueColor,
        dashed: false,
        testId: 'strength-odometer-needle',
      }];

  const seriesLegend = hasSeries ? (
    <div
      data-testid="strength-odometer-legend"
      style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}
    >
      {series.map((entry) => {
        const entryColor = pinColorForScore(entry.gaugeScore, colors);
        const hasValue = !loading && Number.isFinite(entry.value);
        const isActive = entry.key === activeSeriesKey;
        return (
          <button
            type="button"
            key={entry.key}
            data-testid={`strength-odometer-legend-${entry.key}`}
            data-active={isActive ? 'true' : 'false'}
            aria-pressed={entry.key === persistedSeriesKey}
            aria-label={`Show ${entry.label} on chart`}
            onMouseEnter={() => setPreviewSeriesKey(entry.key)}
            onMouseLeave={() => setPreviewSeriesKey(null)}
            onFocus={() => setPreviewSeriesKey(entry.key)}
            onBlur={() => setPreviewSeriesKey(null)}
            onClick={() => setSelectedSeriesKey(entry.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              width: '100%',
              minHeight: 28,
              padding: '3px 7px',
              margin: 0,
              borderRadius: 6,
              border: `1px solid ${isActive ? withAlpha(entryColor, 0.34) : 'transparent'}`,
              backgroundColor: isActive ? withAlpha(entryColor, 0.1) : 'transparent',
              color: colors.text,
              textAlign: 'left',
              cursor: 'pointer',
              outlineOffset: 2,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 14,
                flexShrink: 0,
                borderTop: `2px ${entry.dashed ? 'dashed' : 'solid'} ${hasValue ? entryColor : colors.textMuted}`,
              }}
            />
            <span style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted }}>
              {entry.label}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 800,
                fontVariantNumeric: 'tabular-nums',
                color: hasValue ? entryColor : colors.textMuted,
              }}
            >
              {hasValue ? Math.round(entry.value) : '—'}
            </span>
            {hasValue && entry.strengthLabel ? (
              <span style={{ fontSize: 11, fontWeight: 600, color: entryColor }}>
                {entry.strengthLabel}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  ) : null;

  const meterBar = (
    <div
      style={{
        marginTop: isMini ? 0 : 10,
        opacity: loading ? 0.35 : 1,
        width: isMini ? '100%' : undefined,
      }}
    >
      <StrengthTrack colors={colors} height={isMini ? 4 : 8}>
        <div
          data-testid={isMini ? 'strength-odometer-mini-needle' : 'strength-odometer-needle'}
          style={{
            position: 'absolute',
            top: isMini ? -3 : -4,
            left: `calc(${needlePct}% - ${isMini ? 5 : 6}px)`,
            width: isMini ? 10 : 12,
            height: isMini ? 10 : 16,
            borderRadius: isMini ? 999 : 3,
            zIndex: 1,
            backgroundColor: isMini ? valueColor : colors.text,
            boxShadow: isMini
              ? pinGlow(valueColor, colors.bgPrimary)
              : `0 0 0 2px ${colors.bgCard}`,
            transition: loading ? undefined : 'left 0.45s ease',
          }}
        />
      </StrengthTrack>
    </div>
  );

  if (isMini) {
    return (
      <div
        data-testid="strength-odometer-mini"
        title={titleProp ?? (Number.isFinite(scorePct) ? formatted : undefined)}
        aria-hidden={!Number.isFinite(resolvedScore100)}
        style={{
          width: '100%',
          maxWidth: 72,
          minWidth: 48,
          flexShrink: 1,
          opacity: loading ? 0.35 : 1,
        }}
      >
        {meterBar}
      </div>
    );
  }

  return (
    <div
      data-testid="strength-odometer"
      data-variant={isCircular ? 'circular' : 'bar'}
      style={{
        padding: isCircular ? '12px 16px 14px' : '12px 14px',
        borderRadius: 10,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.bgCard,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: isCircular && !loading
          ? `radial-gradient(ellipse at 50% 78%, ${withAlpha(valueColor, 0.07)} 0%, transparent 52%)`
          : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: hasSeries ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: 10,
          // Narrow cards drop the control below the legend rather than squeezing the readings.
          flexWrap: 'wrap',
          minHeight: 32,
          marginBottom: isCircular ? 2 : 6,
        }}
      >
        {seriesLegend || <div style={{ fontSize: 12, color: colors.textMuted }}>{label}</div>}
        {headerControl}
      </div>
      {isCircular ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: height > 200 ? 280 : 220 }}>
            <CircularStrengthGauge
              needles={needles}
              colors={colors}
              loading={loading || !hasReading}
              width={height > 200 ? 260 : 200}
            />
            {/* The legend carries every series value, so the interactive gauge does not
                repeat an ambiguous centred readout. */}
            {hasSeries ? null : (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '58%',
                  transform: 'translateY(-78%)',
                  textAlign: 'center',
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    fontSize: height > 200 ? 34 : 28,
                    fontWeight: 800,
                    fontVariantNumeric: 'tabular-nums',
                    color: valueColor,
                    lineHeight: 1,
                    letterSpacing: '-0.03em',
                  }}
                >
                  {loading || !Number.isFinite(resolvedScore100) ? '—' : Math.round(display)}
                </div>
                {strengthLabel && !loading && (
                  <div
                    data-testid="strength-odometer-label"
                    style={{
                      marginTop: 4,
                      fontSize: 12,
                      fontWeight: 700,
                      color: valueColor,
                    }}
                  >
                    {strengthLabel}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              color: valueColor,
              lineHeight: 1.1,
            }}
          >
            {loading || !Number.isFinite(resolvedScore100) ? '—' : Math.round(display)}
          </div>
          {strengthLabel && !loading && (
            <div
              data-testid="strength-odometer-label"
              style={{
                marginTop: 4,
                fontSize: 12,
                fontWeight: 600,
                color: valueColor,
              }}
            >
              {strengthLabel}
            </div>
          )}
          {meterBar}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 6,
              fontSize: 11,
              color: colors.textMuted,
            }}
          >
            <span>0</span>
            <span>50</span>
            <span>100</span>
          </div>
        </>
      )}
    </div>
  );
}

export function StrengthScanMeter({
  score100 = null,
  loading = false,
  testId,
}) {
  const { colors } = useTheme();
  const score = Number.isFinite(score100) ? Math.round(score100) : null;
  const valueColor = pinColorForScore(score, colors);
  const needlePct = score == null ? 50 : Math.max(0, Math.min(100, score));

  return (
    <div
      data-testid={testId}
      style={{
        width: '100%',
        opacity: loading ? 0.4 : 1,
      }}
    >
      <StrengthTrack colors={colors} height={4} testId="strength-odometer-mini">
        <div
          data-testid="strength-odometer-mini-needle"
          style={{
            position: 'absolute',
            top: -3,
            left: `calc(${needlePct}% - 5px)`,
            width: 10,
            height: 10,
            borderRadius: 999,
            zIndex: 1,
            backgroundColor: score == null ? colors.textMuted : valueColor,
            boxShadow: pinGlow(score == null ? colors.textMuted : valueColor, colors.bgPrimary),
          }}
        />
      </StrengthTrack>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 5,
          fontSize: 11,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
        }}
      >
        <span style={{ color: colors.textMuted, fontWeight: 500 }}>50</span>
        <span style={{ color: score == null ? colors.textMuted : valueColor, fontWeight: 700 }}>
          {loading || score == null ? '—' : score}
        </span>
      </div>
    </div>
  );
}

export function StrengthSparkline({ scores = [], color, width = 42, height = 16 }) {
  const points = (scores || []).filter(Number.isFinite);
  if (points.length < 2) {
    return (
      <span
        data-testid="relative-strength-sparkline"
        aria-hidden
        style={{ width, height, display: 'inline-block' }}
      />
    );
  }
  const min = Math.min(...points, 40);
  const max = Math.max(...points, 60);
  const span = Math.max(max - min, 1);
  const path = points
    .map((value, index) => {
      const x = (index / (points.length - 1)) * (width - 2) + 1;
      const y = height - 2 - ((value - min) / span) * (height - 4);
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg
      data-testid="relative-strength-sparkline"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden
    >
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function StrengthCombinedPill({
  score100,
  align,
  loading = false,
  colors,
}) {
  const score = Number.isFinite(score100) ? Math.round(score100) : null;
  const tone = align === 'up'
    ? colors.success
    : align === 'down'
      ? colors.danger
      : colors.textMuted;
  const fill = align === 'up'
    ? withAlpha(colors.success, 0.12)
    : align === 'down'
      ? withAlpha(colors.danger, 0.14)
      : (colors.bgSecondary || colors.bgCard || 'rgba(255,255,255,0.06)');
  const stroke = align === 'up'
    ? withAlpha(colors.success, 0.28)
    : align === 'down'
      ? withAlpha(colors.danger, 0.3)
      : (colors.border || 'rgba(255,255,255,0.1)');

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 28,
        minWidth: 40,
        padding: '0 10px',
        borderRadius: 999,
        backgroundColor: fill,
        border: `1px solid ${stroke}`,
        color: loading ? colors.textMuted : tone,
        fontWeight: 700,
        fontSize: 15,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {loading || score == null ? '—' : score}
    </span>
  );
}

export function StrengthScaleLegend({ benchmark = 'SPY' }) {
  const { colors } = useTheme();
  return (
    <div
      data-testid="relative-strength-scale-legend"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 10,
        minWidth: 0,
        flex: '0 1 auto',
        color: colors.textMuted,
        fontSize: 11,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      <span>{`Weaker than ${benchmark}`}</span>
      <span style={{ width: 120, flexShrink: 0 }}>
        <StrengthTrack colors={colors} height={4} />
      </span>
      <span>50</span>
      <span>{`Stronger than ${benchmark}`}</span>
    </div>
  );
}

export default StrengthOdometer;
